import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, Button } from 'antd';
import { ArrowLeftOutlined, CloseOutlined } from '@ant-design/icons';
import '../styles/PdfViewerPage.css';
import { getAuthHeaders } from '../utils/authHeaders';

const PdfViewerPage: React.FC = () => {
  const { type, id } = useParams<{ type: string; id: string }>();
  const navigate = useNavigate();
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const fetchPdfUrl = async () => {
      try {
        let url = '';
        
        if (type === 'demographics') {
          // Direct URL - iframe will handle auth via cookies
          url = `/api/demographics/view-pdf/${id}`;
          console.log('📄 Demographics PDF URL:', url);
        } else if (type === 'insurance') {
          // Direct URL - iframe will handle auth via cookies
          url = `/api/insurance/verifications/${id}/view`;
          console.log('📄 Insurance PDF URL:', url);
        } else if (type === 'order') {
          // For order forms, fetch with auth headers
          try {
            const headers = getAuthHeaders();
            console.log('📄 Fetching order form PDF with headers:', headers);
            
            const response = await fetch(`/api/orders/${id}/order-form`, { 
              headers: headers,
              credentials: 'include'
            });
            
            console.log('📄 Order form response status:', response.status);
            
            if (response.status === 401 || response.status === 403) {
              // Redirect to login if not authenticated
              window.location.href = '/login';
              return;
            }
            
            if (response.ok && response.headers.get('content-type') === 'application/pdf') {
              const blob = await response.blob();
              url = URL.createObjectURL(blob);
            } else {
              const responseText = await response.text();
              console.error('📄 Order form response error:', responseText);
              throw new Error('Failed to load order form PDF');
            }
          } catch (fetchError) {
            console.error('📄 Error fetching order form:', fetchError);
            throw fetchError;
          }
        } else if (type === 'invoice') {
          // Direct URL - iframe will handle auth via cookies
          url = `/api/invoices/${id}/download`;
          console.log('📄 Invoice PDF URL:', url);
        }
        
        console.log('📄 Setting PDF URL:', url);
        setPdfUrl(url);
        setLoading(false);
      } catch (err) {
        console.error('📄 PDF viewer error:', err);
        setError(`Failed to load PDF: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setLoading(false);
      }
    };

    if (id) {
      fetchPdfUrl();
    }
  }, [type, id]);

  const handleClose = () => {
    window.close();
  };

  if (loading) {
    return (
      <div className="pdf-viewer-container">
        <div className="pdf-viewer-loading">
          <Spin size="large" />
          <p>Loading PDF...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pdf-viewer-container">
        <div className="pdf-viewer-error">
          <p>{error}</p>
          <Button onClick={handleClose} icon={<CloseOutlined />}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pdf-viewer-container">
      <div className="pdf-viewer-header">
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate(-1)}
          style={{ marginRight: '10px' }}
        >
          Back
        </Button>
        <Button 
          icon={<CloseOutlined />} 
          onClick={handleClose}
        >
          Close
        </Button>
      </div>
      <div className="pdf-viewer-content">
        <iframe
          src={pdfUrl}
          title="PDF Viewer"
          style={{
            width: '100%',
            height: '100%',
            border: 'none'
          }}
          onLoad={() => console.log('📄 Iframe loaded successfully')}
          onError={(e) => console.error('📄 Iframe error:', e)}
        />
      </div>
    </div>
  );
};

export default PdfViewerPage;
